import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Image as ImageIcon,
  Upload,
  Trash2,
  Search,
  X,
  Check,
  Copy,
  Loader2,
  CheckSquare,
  FolderOpen,
  Share2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMedia, uploadMedia, deleteMedia, deleteMultipleMedia, formatFileSize } from '../services/mediaService';
import { createTodo } from '../services/todoService';
import { CreateTodoModal } from '../components/todos';
import { SharedMediaGrid } from '../components/media';
import { Toast } from '../components/ui';
import type { Media as MediaType } from '../types/media';
import type { CreateTodoInput } from '../types/todo';
import { logger } from '../lib/logger';

type MediaTab = 'my-media' | 'shared-media';

export function Media() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<MediaTab>('my-media');
  const [mediaList, setMediaList] = useState<MediaType[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedMedia, setSelectedMedia] = useState<MediaType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [showTodoToast, setShowTodoToast] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      loadMedia();
    }
  }, [user]);

  const loadMedia = async () => {
    if (!user) return;
    try {
      const data = await getMedia(user.id);
      setMediaList(data);
    } catch (error) {
      logger.error('Error loading media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || !user) return;

      setUploading(true);

      try {
        for (const file of Array.from(files)) {
          if (!file.type.startsWith('image/')) continue;
          if (file.size > 10 * 1024 * 1024) continue;

          await uploadMedia(user.id, file);
        }
        await loadMedia();
      } catch (error) {
        logger.error('Upload error:', error);
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [user]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (!files || !user) return;

      setUploading(true);

      try {
        for (const file of Array.from(files)) {
          if (!file.type.startsWith('image/')) continue;
          if (file.size > 10 * 1024 * 1024) continue;

          await uploadMedia(user.id, file);
        }
        await loadMedia();
      } catch (error) {
        logger.error('Upload error:', error);
      } finally {
        setUploading(false);
      }
    },
    [user]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredMedia.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMedia.map((m) => m.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (!user || selectedIds.size === 0) return;

    if (!confirm(`${selectedIds.size} Datei(en) wirklich löschen?`)) return;

    setDeleting(true);
    try {
      await deleteMultipleMedia(Array.from(selectedIds), user.id);
      setSelectedIds(new Set());
      await loadMedia();
    } catch (error) {
      logger.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (media: MediaType) => {
    if (!user) return;
    if (!confirm('Dieses Bild wirklich löschen?')) return;

    setDeleting(true);
    try {
      await deleteMedia(media.id, user.id);
      setSelectedMedia(null);
      await loadMedia();
    } catch (error) {
      logger.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Copy error:', error);
    }
  };

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user) return;
    await createTodo(user.id, input);
    setIsTodoModalOpen(false);
    setShowTodoToast(true);
  };

  const filteredMedia = search
    ? mediaList.filter(
        (m) =>
          m.filename.toLowerCase().includes(search.toLowerCase()) ||
          m.alt_text?.toLowerCase().includes(search.toLowerCase())
      )
    : mediaList;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTodoToast && (
        <Toast
          message="Todo erfolgreich erstellt!"
          linkText="Zu den Todos"
          linkTo="/todos"
          onClose={() => setShowTodoToast(false)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white">Medien</h1>
          <p className="text-[#6B6B6B] dark:text-slate-400">
            {activeTab === 'my-media'
              ? `${mediaList.length} ${mediaList.length === 1 ? 'Datei' : 'Dateien'}`
              : 'Von Admins geteilte Medien'}
          </p>
        </div>
        {activeTab === 'my-media' && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-medium hover:bg-primary-dark transition-all disabled:opacity-50 w-full sm:w-auto"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
            {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 dark:bg-slate-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('my-media')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
            activeTab === 'my-media'
              ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          Meine Medien
        </button>
        <button
          onClick={() => setActiveTab('shared-media')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
            activeTab === 'shared-media'
              ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Share2 className="w-4 h-4" />
          Geteilte Medien
        </button>
      </div>

      {/* Shared Media Tab */}
      {activeTab === 'shared-media' && <SharedMediaGrid />}

      {/* My Media Tab */}
      {activeTab === 'my-media' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen..."
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:border-primary focus:outline-none bg-white dark:bg-slate-800 dark:text-white"
              />
            </div>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-slate-400">{selectedIds.size} ausgewählt</span>
                <button
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="p-2.5 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            {filteredMedia.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  {search ? 'Keine Ergebnisse' : 'Keine Medien'}
                </h3>
                <p className="text-gray-500 dark:text-slate-400 mb-4">
                  {search
                    ? 'Versuche einen anderen Suchbegriff'
                    : 'Lade dein erstes Bild hoch'}
                </p>
                {!search && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                  >
                    <Upload className="w-4 h-4" />
                    Bild hochladen
                  </button>
                )}
              </div>
            ) : (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={selectAll}
                    className="text-sm text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    {selectedIds.size === filteredMedia.length ? 'Auswahl aufheben' : 'Alle auswählen'}
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {filteredMedia.map((media) => (
                    <div
                      key={media.id}
                      className={`relative group aspect-square rounded-xl overflow-hidden cursor-pointer border-2 transition-colors ${
                        selectedIds.has(media.id)
                          ? 'border-primary'
                          : 'border-transparent hover:border-gray-200'
                      }`}
                      onClick={() => setSelectedMedia(media)}
                    >
                      <img
                        src={media.url}
                        alt={media.alt_text || media.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelect(media.id);
                        }}
                        className={`absolute top-2 left-2 w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                          selectedIds.has(media.id)
                            ? 'bg-primary text-white'
                            : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {selectedMedia && (
        <MediaDetailModal
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          onDelete={() => handleDeleteSingle(selectedMedia)}
          onCopyUrl={() => copyUrl(selectedMedia.url)}
          onCreateTodo={() => setIsTodoModalOpen(true)}
          copied={copied}
          deleting={deleting}
        />
      )}

      {/* Todo Modal */}
      {user && selectedMedia && (
        <CreateTodoModal
          isOpen={isTodoModalOpen}
          onClose={() => setIsTodoModalOpen(false)}
          onSubmit={handleCreateTodo}
          defaultTitle={`Bild: ${selectedMedia.filename}`}
          defaultDescription="Bild für Social Media verwenden"
          tag="general"
          sourceType="media"
          sourceId={selectedMedia.id}
        />
      )}
    </div>
  );
}

function MediaDetailModal({
  media,
  onClose,
  onDelete,
  onCopyUrl,
  onCreateTodo,
  copied,
  deleting,
}: {
  media: MediaType;
  onClose: () => void;
  onDelete: () => void;
  onCopyUrl: () => void;
  onCreateTodo: () => void;
  copied: boolean;
  deleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate pr-2">{media.filename}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="aspect-video bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
            <img
              src={media.url}
              alt={media.alt_text || media.filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-slate-400 mb-1">Dateiname</p>
                <p className="font-medium text-gray-900 dark:text-white truncate text-xs sm:text-sm">{media.filename}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-slate-400 mb-1">Dateigröße</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatFileSize(media.size)}</p>
              </div>
              {media.width && media.height && (
                <div>
                  <p className="text-gray-500 dark:text-slate-400 mb-1">Abmessungen</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {media.width} × {media.height} px
                  </p>
                </div>
              )}
              <div>
                <p className="text-gray-500 dark:text-slate-400 mb-1">Hochgeladen</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(media.created_at).toLocaleDateString('de-DE')}
                </p>
              </div>
            </div>

            <div>
              <p className="text-gray-500 dark:text-slate-400 mb-1 text-sm">URL</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={media.url}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-300 min-w-0"
                />
                <button
                  onClick={onCopyUrl}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-white bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Kopiert' : 'Kopieren'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <button
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Löschen
            </button>
            <button
              onClick={onCreateTodo}
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-white bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              <CheckSquare className="w-4 h-4" />
              Todo
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
